import {useState, type ReactNode} from 'react';
import {Loading, dateTime} from './components';
import {APP_BASE, Link} from './router';
import './workspace-ui.css';

export type WorkspacePage = 'workspace' | 'tasks' | 'files' | 'settings';
export interface WorkChatRow {
  id: string;
  title: string;
  preview: string | null;
  updatedAt: string | null;
}

function topicFor(title: string): 'website' | 'research' | 'calendar' | 'pitch' | null {
  if (/\b(website|web\s?page|launch|site)\b/i.test(title)) return 'website';
  if (/\b(research|market|compare|analysis|analyse|analyze)\b/i.test(title)) return 'research';
  if (/\b(week|plan|schedule|calendar)\b/i.test(title)) return 'calendar';
  if (/\b(pitch|presentation|slides|proposal)\b/i.test(title)) return 'pitch';
  return null;
}

function ChatTopic({title}: {title: string}) {
  const topic = topicFor(title);
  return <span className="workspace-chat-topic" aria-hidden="true">{topic
    ? <img src={`${APP_BASE}/assets/chat-topic-${topic}-24.png`} width={24} height={24} alt=""/>
    : <span className="workspace-chat-initial">{title.trim().slice(0, 1).toLocaleUpperCase()}</span>}</span>;
}

function chatTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error(`Invalid work chat date: ${value}`);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'}).format(date);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'}).format(date);
}

export function WorkspaceSidebar({chats, selectedChatId, onSelect, onCreate, busy = false, createDisabled = false,
  loading = false, status, footer, settingsHref, page}: {
  chats: readonly WorkChatRow[]; selectedChatId: string | null; onSelect: (id: string) => void;
  onCreate: () => void; busy?: boolean; createDisabled?: boolean; loading?: boolean;
  status?: ReactNode; footer: ReactNode; settingsHref: string;
  page: WorkspacePage;
}) {
  const [search, setSearch] = useState('');
  const rows = chats.filter(chat => `${chat.title} ${chat.preview ?? ''}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const today = new Date().toDateString();
  const groups = rows.reduce<{label: string; rows: WorkChatRow[]}[]>((result, row) => {
    const label = search.trim() ? 'Search results'
      : row.updatedAt !== null && new Date(row.updatedAt).toDateString() === today ? 'Today' : 'Work chats';
    const group = result.find(item => item.label === label);
    if (group) group.rows.push(row); else result.push({label, rows: [row]});
    return result;
  }, []);
  return <aside className="workspace-sidebar" aria-label="Work chats">
    <div className="workspace-sidebar-top"><h2>Work chats</h2>
      <button type="button" className="workspace-new-chat" disabled={busy || createDisabled} onClick={onCreate}>
        <span aria-hidden="true">+</span>New chat</button>
      <label className="workspace-search"><span className="visually-hidden">Search chats</span>
        <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search chats"/></label>
    </div>
    <div className="workspace-sidebar-chats">{status}{loading && chats.length === 0 && <Loading/>}
      <nav aria-label="Saved work chats">{groups.map(group => <section className="workspace-chat-group" key={group.label}>
        <h2>{group.label}</h2>{group.rows.map(row => <button type="button" key={row.id}
          className={`workspace-chat-row${selectedChatId === row.id ? ' is-current' : ''}`} aria-current={selectedChatId === row.id ? 'page' : undefined}
          onClick={() => onSelect(row.id)} disabled={busy}>
          <ChatTopic title={row.title}/><span className="workspace-chat-copy"><strong>{row.title}</strong>
            {row.preview !== null && <small>{row.preview}</small>}</span>
          {row.updatedAt && <time dateTime={row.updatedAt} title={dateTime(row.updatedAt)}>{chatTime(row.updatedAt)}</time>}
        </button>)}</section>)}</nav>
      {!loading && rows.length === 0 && <p className="workspace-sidebar-empty">{search.trim() ? 'No chats match your search.' : 'Your chats will appear here.'}</p>}
    </div>
    <div className="workspace-sidebar-footer"><Link className={`workspace-settings-link${page === 'settings' ? ' is-current' : ''}`} href={settingsHref}>Settings</Link>{footer}</div>
  </aside>;
}

export function WorkspaceShell({sidebar, conversation, children, stackedConversation = false, className = ''}: {
  sidebar: ReactNode; conversation?: ReactNode; children: ReactNode; stackedConversation?: boolean; className?: string;
}) {
  return <div className={`workspace-shell ${conversation ? (stackedConversation ? 'has-stacked-conversation ' : 'has-conversation ') : ''}${className}`}>{sidebar}
    <div className="workspace-main"><div className="workspace-body">{children}</div>{stackedConversation && conversation}</div>
    {!stackedConversation && conversation}</div>;
}
